import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Wallet, ArrowLeftRight, Map, Settings, Bell, Search, Menu, X, History, Check, Trash2, BookOpen, TrendingUp, LogOut, Repeat, ShieldAlert, Loader2, MessageCircle } from 'lucide-react';
import Logo from './Logo';
import api from '../api';
import StripeCheckoutModal from './StripeCheckoutModal';
import SupportModal from './SupportModal';

export default function Layout() {
  const location = useLocation();
  const [profileName, setProfileName] = useState('Alex');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const updateAuth = () => {
      const stored = localStorage.getItem('thread_user');
      if (stored) {
        setUser(JSON.parse(stored));
      }
    };
    updateAuth();
    window.addEventListener('thread_auth_updated', updateAuth);
    return () => window.removeEventListener('thread_auth_updated', updateAuth);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setShowMoreMenu(false);
  }, [location.pathname]);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data);
    } catch (err) {}
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Check every minute
    window.addEventListener('thread_notifications_updated', fetchNotifications);
    return () => {
      clearInterval(interval);
      window.removeEventListener('thread_notifications_updated', fetchNotifications);
    };
  }, []);

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      fetchNotifications();
    } catch (err) {}
  };

  const clearAll = async () => {
    try {
      await api.delete('/notifications/clear');
      fetchNotifications();
    } catch (err) {}
  };

  useEffect(() => {
    const updateName = () => {
      const name = localStorage.getItem('thread_name');
      if (name) setProfileName(name);
    };
    updateName();
    window.addEventListener('thread_settings_updated', updateName);
    return () => window.removeEventListener('thread_settings_updated', updateName);
  }, []);

  const navItems = [
    { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    { name: 'Buckets', path: '/funds', icon: Wallet },
    { name: 'Paycheck Rules', path: '/allocations', icon: ArrowLeftRight },
    { name: 'Money Map', path: '/map', icon: Map },
    { name: 'Activity', path: '/ledger', icon: History },
    { name: 'Bills & Subs', path: '/subscriptions', icon: Repeat },
  ];

  if (user && user.is_admin) {
    navItems.push({ name: 'Admin Panel', path: '/admin', icon: ShieldAlert });
  }

  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [checkoutClientSecret, setCheckoutClientSecret] = useState(null);
  const [showSupportModal, setShowSupportModal] = useState(false);

  const handleSubscribe = async () => {
    setLoadingCheckout(true);
    try {
      const res = await api.post('/stripe/create-checkout-session');
      setCheckoutClientSecret(res.data.clientSecret);
      setLoadingCheckout(false);
    } catch (err) {
      console.error(err);
      setLoadingCheckout(false);
    }
  };

  let trialBanner = null;
  if (user && user.subscription_status === 'trialing' && user.trial_ends_at) {
    const trialEnd = new Date(user.trial_ends_at);
    const now = new Date();
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft > 0) {
      trialBanner = (
        <div style={{ padding: '0.8rem 1rem', background: 'rgba(59, 130, 246, 0.1)', border: '1px solid var(--primary)', borderRadius: 'var(--radius-md)', color: 'var(--text-main)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <span><strong style={{ color: 'var(--primary)' }}>Free Trial:</strong> You have {daysLeft} days left in your trial.</span>
          <button onClick={handleSubscribe} disabled={loadingCheckout} className="btn btn-primary" style={{ padding: '0.4rem 1rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {loadingCheckout && <Loader2 size={14} style={{ animation: 'spin 2s linear infinite' }} />} Upgrade to Pro
          </button>
        </div>
      );
    } else {
      trialBanner = (
        <div style={{ padding: '0.8rem 1rem', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--danger)', borderRadius: 'var(--radius-md)', color: 'var(--text-main)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <span><strong style={{ color: 'var(--danger)' }}>Trial Expired:</strong> Your automated routing is paused. Upgrade to resume.</span>
          <button onClick={handleSubscribe} disabled={loadingCheckout} className="btn btn-primary" style={{ padding: '0.4rem 1rem', fontSize: '0.85rem', background: 'var(--danger)', borderColor: 'var(--danger)', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {loadingCheckout && <Loader2 size={14} style={{ animation: 'spin 2s linear infinite' }} />} Upgrade to Pro
          </button>
        </div>
      );
    }
  }

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      
      {/* Sidebar Navigation */}
      <aside className="glass desktop-sidebar hide-on-mobile" style={{ width: '260px', margin: 'var(--spacing-md)', display: 'flex', flexDirection: 'column', flexShrink: 0, border: '1px solid rgba(255, 255, 255, 0.05)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', borderRadius: 'var(--radius-lg)', background: 'rgba(255, 255, 255, 0.01)' }}>
        <div style={{ padding: 'var(--spacing-lg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 className="text-gradient" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '1.4rem' }}>
            <Logo size={28} /> Thread
          </h2>
        </div>
        
        <nav style={{ padding: '0 var(--spacing-md)', flex: 1 }}>
          <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xs)' }}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link 
                    to={item.path} 
                    className="btn btn-glass" 
                    style={{ 
                      width: '100%', 
                      justifyContent: 'flex-start', 
                      background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                      borderColor: isActive ? 'var(--primary)' : 'transparent' 
                    }}
                  >
                    <item.icon size={18} /> {item.name}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div style={{ padding: 'var(--spacing-md)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <Link to="/analytics" className="btn btn-glass" style={{ width: '100%', justifyContent: 'flex-start', border: 'none', textDecoration: 'none' }}>
            <TrendingUp size={18} /> Analytics
          </Link>
          <button onClick={() => setShowSupportModal(true)} className="btn btn-glass" style={{ width: '100%', justifyContent: 'flex-start', border: 'none', color: 'inherit', cursor: 'pointer' }}>
            <MessageCircle size={18} /> Support
          </button>
          <Link to="/settings" className="btn btn-glass" style={{ width: '100%', justifyContent: 'flex-start', border: 'none', textDecoration: 'none' }}>
            <Settings size={18} /> Settings
          </Link>
          <button 
            className="btn btn-glass hover-danger" 
            style={{ width: '100%', justifyContent: 'flex-start', border: 'none', color: 'var(--text-muted)' }}
            onClick={async () => {
              try {
                await api.post('/auth/logout');
              } catch (err) {}
              localStorage.removeItem('thread_user');
              window.dispatchEvent(new Event('thread_auth_error'));
            }}
          >
            <LogOut size={18} /> Logout
          </button>
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1rem', fontSize: '0.75rem', flexWrap: 'wrap' }}>
            <Link to="/knowledge" className="text-muted" style={{ textDecoration: 'none' }}>Knowledge Base</Link>
            <Link to="/privacy" className="text-muted" style={{ textDecoration: 'none' }}>Privacy Policy</Link>
            <Link to="/terms" className="text-muted" style={{ textDecoration: 'none' }}>Terms</Link>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <main style={{ flex: 1, padding: 'var(--spacing-md) var(--spacing-md) var(--spacing-md) 0', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-md)', overflowY: 'auto', overflowX: 'hidden' }}>
        
        {/* Global Header */}
        <header className="glass" style={{ padding: '1rem var(--spacing-lg)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, border: '1px solid rgba(255, 255, 255, 0.05)', borderRadius: 'var(--radius-lg)', background: 'rgba(255, 255, 255, 0.01)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div>
              <h3 style={{ margin: 0 }}>Welcome back, {profileName}</h3>
              <span className="text-muted hide-on-mobile" style={{ fontSize: '0.9rem' }}>Here is your financial overview.</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
            <div style={{ position: 'relative' }}>
              <button className="btn btn-glass" style={{ padding: '0.6rem', position: 'relative' }} onClick={() => setShowNotifs(!showNotifs)}>
                <Bell size={18} />
                {notifications.filter(n => !n.is_read).length > 0 && (
                  <span style={{ position: 'absolute', top: '-5px', right: '-5px', background: 'var(--danger)', color: 'white', borderRadius: '50%', width: '18px', height: '18px', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                    {notifications.filter(n => !n.is_read).length}
                  </span>
                )}
              </button>
            </div>
            <Link to="/settings" style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(to right, var(--primary), var(--secondary))', display: 'flex', cursor: 'pointer', transition: 'transform 0.2s ease', ':hover': { transform: 'scale(1.05)' } }} title="Settings"></Link>
          </div>
        </header>

        {trialBanner}

        {/* Page Content (Outlet) */}
        <Outlet />

        {/* Global Footer for Authenticated App */}
        <footer style={{ marginTop: 'auto', padding: '2rem 1rem', textAlign: 'center', borderTop: '1px solid var(--surface-border)', opacity: 0.7 }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
            <button onClick={() => setShowSupportModal(true)} style={{ color: 'var(--text-muted)', background: 'transparent', border: 'none', padding: 0, fontSize: '0.85rem', cursor: 'pointer' }}>Support</button>
            <Link to="/knowledge" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.85rem' }}>Knowledge Base</Link>
            <Link to="/terms" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.85rem' }}>Terms</Link>
            <Link to="/privacy" style={{ color: 'var(--text-muted)', textDecoration: 'none', fontSize: '0.85rem' }}>Privacy</Link>
          </div>
          <p style={{ margin: 0, fontSize: '0.75rem', color: 'var(--text-muted)' }}>© {new Date().getFullYear()} Thread Finance. All rights reserved.</p>
        </footer>

        <StripeCheckoutModal 
          clientSecret={checkoutClientSecret} 
          onClose={() => setCheckoutClientSecret(null)} 
        />

        {/* Floating Support Button */}
        <button 
          onClick={() => setShowSupportModal(true)}
          className="btn btn-primary hide-on-mobile"
          style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            borderRadius: '50px',
            padding: '0.8rem 1.5rem',
            boxShadow: '0 10px 25px -5px rgba(59, 130, 246, 0.5)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            zIndex: 90,
            cursor: 'pointer'
          }}
        >
          <MessageCircle size={20} /> Support
        </button>

        {showSupportModal && <SupportModal onClose={() => setShowSupportModal(false)} />}
      </main>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="mobile-bottom-nav">
        {[navItems[0], navItems[1], navItems[2], navItems[3]].map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link key={item.path} to={item.path} className={`nav-item-mobile ${isActive ? 'active' : ''}`}>
              <item.icon size={22} />
              <span>{item.name}</span>
            </Link>
          );
        })}
        <button 
          className={`nav-item-mobile ${showMoreMenu ? 'active' : ''}`} 
          style={{ background: 'transparent', border: 'none', cursor: 'pointer' }} 
          onClick={() => setShowMoreMenu(!showMoreMenu)}
        >
          <Menu size={22} />
          <span>Menu</span>
        </button>
      </nav>

      {/* Mobile More Menu Overlay */}
      {showMoreMenu && (
        <>
          <div 
            className="show-on-mobile"
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 98 }} 
            onClick={() => setShowMoreMenu(false)}
          ></div>
          <div className="mobile-more-overlay show-on-mobile">
            <Link to="/analytics" className="btn btn-glass" style={{ justifyContent: 'flex-start', border: 'none' }}>
              <TrendingUp size={18} /> Analytics
            </Link>
            <Link to="/knowledge" className="btn btn-glass" style={{ justifyContent: 'flex-start', border: 'none' }}>
              <BookOpen size={18} /> Knowledge Base
            </Link>
            <div style={{ height: '1px', background: 'var(--surface-border)', margin: '0.25rem 0' }}></div>
            <button onClick={() => { setShowSupportModal(true); setShowMoreMenu(false); }} className="btn btn-glass" style={{ justifyContent: 'flex-start', border: 'none', color: 'inherit', width: '100%' }}>
              <MessageCircle size={18} /> Support
            </button>
            <Link to="/settings" className="btn btn-glass" style={{ justifyContent: 'flex-start', border: 'none' }}>
              <Settings size={18} /> Settings
            </Link>
            <button 
              className="btn btn-glass hover-danger" 
              style={{ justifyContent: 'flex-start', border: 'none', color: 'var(--text-muted)' }}
              onClick={async () => {
                try { await api.post('/auth/logout'); } catch (err) {}
                localStorage.removeItem('thread_user');
                window.dispatchEvent(new Event('thread_auth_error'));
              }}
            >
              <LogOut size={18} /> Logout
            </button>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', gap: '1rem', fontSize: '0.75rem' }}>
              <Link to="/privacy" className="text-muted" style={{ textDecoration: 'none' }}>Privacy Policy</Link>
              <Link to="/terms" className="text-muted" style={{ textDecoration: 'none' }}>Terms of Service</Link>
            </div>
          </div>
        </>
      )}

      {/* Notifications Backdrop */}
      {showNotifs && (
        <div 
          className="animate-fade-in"
          style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)', zIndex: 999 }} 
          onClick={() => setShowNotifs(false)}
        ></div>
      )}

      {/* Notifications Panel */}
      <div 
        style={{ 
          position: 'fixed', top: 0, right: 0, width: '380px', maxWidth: '100vw', height: '100vh', zIndex: 1000, 
          display: 'flex', flexDirection: 'column', 
          transform: showNotifs ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
          boxShadow: showNotifs ? '-20px 0 50px rgba(0,0,0,0.8)' : 'none',
          padding: '1.5rem',
          borderLeft: '1px solid var(--surface-border)',
          background: '#020617', // Solid dark tailwind slate-950
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><Bell size={20} /> Notifications</h3>
          <button className="btn btn-glass" style={{ padding: '0.4rem', border: 'none' }} onClick={() => setShowNotifs(false)}>
            <X size={20} />
          </button>
        </div>
        
        {notifications.length > 0 && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            {notifications.filter(n => !n.is_read).length > 0 && (
              <button className="btn btn-glass" style={{ flex: 1, justifyContent: 'center', fontSize: '0.85rem', padding: '0.5rem' }} onClick={markAllRead}>
                <Check size={14} /> Read All
              </button>
            )}
            <button className="btn btn-glass" style={{ flex: 1, justifyContent: 'center', fontSize: '0.85rem', padding: '0.5rem', color: 'var(--danger)', borderColor: 'rgba(239, 68, 68, 0.2)' }} onClick={clearAll}>
              <Trash2 size={14} /> Clear All
            </button>
          </div>
        )}

        {notifications.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, color: 'var(--text-muted)' }}>
            <Bell size={48} style={{ opacity: 0.2, marginBottom: '1rem' }} />
            <p>You're all caught up!</p>
          </div>
        ) : (
          <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingRight: '0.5rem' }}>
            {notifications.map(n => (
              <div 
                key={n.id} 
                style={{ 
                  flexShrink: 0,
                  padding: '1rem', 
                  background: n.is_read ? 'rgba(255,255,255,0.02)' : 'linear-gradient(145deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.05))', 
                  border: '1px solid',
                  borderColor: n.is_read ? 'var(--surface-border)' : 'var(--primary)', 
                  borderRadius: '12px',
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {!n.is_read && (
                  <div style={{ position: 'absolute', top: 0, left: 0, width: '4px', height: '100%', background: 'var(--primary)', boxShadow: '0 0 10px var(--primary)' }}></div>
                )}
                <p style={{ margin: '0 0 0.5rem 0', fontWeight: 600, fontSize: '0.95rem', color: n.is_read ? 'var(--text)' : 'white' }}>{n.title}</p>
                <p className="text-muted" style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5 }}>{n.message}</p>
                <small className="text-muted" style={{ display: 'block', marginTop: '0.75rem', fontSize: '0.75rem' }}>
                  {new Date(n.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                </small>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
